import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { Container, Card, Row, Col, Badge, Button, Table } from "react-bootstrap";
import { ClockHistory } from "react-bootstrap-icons";
import HistoryModal from "./components/history/HistoryModal";

export default function RequestDetail() {
  const { requestNumber } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [lookups, setLookups] = useState({
    languages: {},
    formats: {},
    projects: {},
    types: {},
  });

  function formatTaskDate(dateString) {
    if (!dateString) return "TBD";
    const d = new Date(dateString);
    const datePart = d.toISOString().split("T")[0];
    const dayPart = d.toLocaleDateString("en-US", { weekday: "short" });
    const timePart = d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${datePart} ${dayPart} ${timePart}`;
  }

  function getTimeRemaining(deadline) {
    if (!deadline) return "No deadline set";
    const total = Date.parse(deadline) - Date.parse(new Date());
    if (total <= 0) return "⚠️ Overdue";
    const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
    const days = Math.floor(total / (1000 * 60 * 60 * 24));
    return `${days}d ${hours}h left`;
  }

  async function openPrivateFile(fileObject) {
    const filePath = fileObject.path || fileObject.url;
    if (!filePath) {
      alert("Error: File path not found.");
      return;
    }
    try {
      const { data, error } = await supabase.storage
        .from("request-files")
        .createSignedUrl(filePath, 3600);
      if (error) throw error;
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Error opening file:", err);
      alert("Could not open secure link: " + err.message);
    }
  }

  useEffect(() => {
  async function fetchData() {
    const { data: reqData, error: reqError } = await supabase
      .from("requests")
      .select(`
        *,
        jobs (
          *,
          tasks (
            *,
            languages (name),
            vendors (name)
          )
        )
      `)
      .eq("request_number", Number(requestNumber))
      .single();

    if (reqError) {
      console.error("Fetch error:", reqError);
      return;
    }

    const [langs, forms, projs, types] = await Promise.all([
      supabase.from("languages").select("id, name"),
      supabase.from("formats").select("id, name"),
      supabase.from("projects").select("id, name"),
      supabase.from("content_types").select("id, name"),
    ]);

    const createMap = (data) =>
      data?.reduce((acc, curr) => ({ ...acc, [curr.id]: curr.name }), {}) || {};

    setLookups({
      languages: createMap(langs.data),
      formats: createMap(forms.data),
      projects: createMap(projs.data),
      types: createMap(types.data),
    });

    setRequest(reqData);
  }

  fetchData();
}, [requestNumber]);

  if (!request)
    return <Container className="mt-5 text-center">Loading request details...</Container>;

  return (
    <Container className="mt-5 mb-5">
      <Button
        variant="link"
        onClick={() => navigate("/dashboard")}
        className="mb-3 p-0 text-decoration-none text-muted"
      >
        ← Back to Dashboard
      </Button>

      <Card className="shadow-sm border-0">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h2 className="fw-bold mb-1">{request.title}</h2>
              <p className="text-muted">
                Request #{request.request_number} • Submitted on{" "}
                {new Date(request.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Badge pill bg={request.status === "completed" ? "success" : "primary"} className="px-3 py-2 text-uppercase">
                {request.status}
              </Badge>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowHistory(true)}
                className="d-flex align-items-center"
              >
                <ClockHistory className="me-1" /> History
              </Button>
            </div>
          </div>

          <h6 className="text-uppercase text-muted small fw-bold">Description</h6>
          <div className="p-3 bg-light rounded border mb-4" style={{ whiteSpace: "pre-wrap" }}>
            {request.description || request.text}
          </div>

          <Row className="mb-4">
            <Col md={6} className="mb-3">
              <h6 className="text-uppercase text-muted small fw-bold">Source Language</h6>
              <p>{lookups.languages[request.source_language] || "N/A"}</p>
            </Col>
            <Col md={6} className="mb-3">
              <h6 className="text-uppercase text-muted small fw-bold">Deadline</h6>
              <div className="d-flex flex-column">
                <span className="fw-bold font-monospace" style={{ fontSize: "0.9rem" }}>
                  {formatTaskDate(request.deadline)}
                </span>
                {request.deadline && (
                  <small
                    className={
                      Date.parse(request.deadline) < Date.now() ? "text-danger fw-bold" : "text-muted"
                    }
                  >
                    {getTimeRemaining(request.deadline)}
                  </small>
                )}
              </div>
            </Col>
          </Row>

          <hr />

          <Row className="mt-4">
            <Col md={8}>
              {request.attachments?.length > 0 && (
                <div className="mb-5">
                  <h6 className="text-uppercase text-muted small fw-bold mb-3">Project Files</h6>
                  <div className="row g-2">
                    {request.attachments.map((file, idx) => (
                      <div key={idx} className="col-md-6">
                        <div className="p-2 border rounded bg-white d-flex align-items-center justify-content-between">
                          <div className="text-truncate me-2">
                            <span className="small d-block text-truncate">{file.name}</span>
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openPrivateFile(file)}
                          >
                            View File ↗
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {request.jobs?.length > 0 && (
                <div className="mt-2">
                  <h5 className="fw-bold mb-3">Project Workflow & Tasks</h5>
                  {request.jobs.map((job) => (
                    <Card key={job.id} className="border-0 shadow-sm mb-4 bg-light rounded-3">
                      <Card.Body className="p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                          <div className="d-flex align-items-center flex-wrap gap-2">
                            <Badge bg="primary" className="px-3 py-2 fs-6">
                              Job #{job.job_number}
                            </Badge>
                            <h6 className="mb-0 fw-bold text-dark">{job.job_title}</h6>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <Badge bg="dark" className="text-uppercase">{job.status ?? "—"}</Badge>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => navigate(`/request/${requestNumber}/job/${job.job_number}`)}
                            >
                              Open job →
                            </Button>
                          </div>
                        </div>

                        <Table responsive hover className="bg-white rounded overflow-hidden">
                          <thead className="table-light">
                            <tr>
                              <th>Language</th>
                              <th>Task Type</th>
                              <th>Assigned Vendor</th>
                              <th>Status</th>
                              <th>Deadline</th>
                            </tr>
                          </thead>
                          <tbody>
                            {job.tasks?.length > 0 ? (
                                job.tasks.map((task) => (
                                <tr key={task.id}>
                                    <td className="fw-bold">{task.languages?.name}</td>
                                    <td>{task.task_type}</td>
                                    <td>
                                    {task.vendors?.name ? (
                                        <Badge bg="light" text="dark" className="border">
                                        🏢 {task.vendors.name}
                                        </Badge>
                                    ) : (
                                        <span className="text-muted small italic">Auto-assigning...</span>
                                    )}
                                    </td>
                                    <td>
                                    <Badge
                                        pill
                                        bg={task.status === "completed" ? "success" : "secondary"}
                                    >
                                        {task.status}
                                    </Badge>
                                    </td>
                                    <td>
                                    {task.deadline ? new Date(task.deadline).toLocaleDateString() : "TBD"}
                                    </td>
                                </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center text-muted small py-3 italic">
                                        Tasks are being generated or assigned.
                                    </td>
                                </tr>
                            )}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
              <h6 className="text-uppercase text-muted small fw-bold">Notes</h6>
              <p className="text-muted small">{request.notes || "No additional notes provided."}</p>
            </Col>

            <Col md={4} className="border-start ps-4">
              <div className="mb-4">
                <h6 className="text-uppercase text-muted small fw-bold">Project</h6>
                <p>{lookups.projects[request.project_id] || "General"}</p>
              </div>
              <div className="mb-4">
                <h6 className="text-uppercase text-muted small fw-bold">Target Locales</h6>
                {request.target_languages?.length ? (
                  <div className="d-flex flex-wrap gap-1">
                    {request.target_languages.map((langId) => (
                      <Badge key={langId} bg="info" className="fw-normal text-white">
                        {lookups.languages[langId] || langId}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted small mb-0">No target locales specified.</p>
                )}
              </div>
              <div className="mb-4">
                <h6 className="text-uppercase text-muted small fw-bold">Format(s)</h6>
                {request.format_ids?.map((fid) => (
                  <Badge key={fid} bg="secondary" className="me-1 fw-normal">
                    {lookups.formats[fid]}
                  </Badge>
                ))}
              </div>
              <div className="mb-4">
                <h6 className="text-uppercase text-muted small fw-bold">Word Count</h6>
                <p>{request.word_count?.toLocaleString() || 0} words</p>
              </div>
              <div>
                <h6 className="text-uppercase text-muted small fw-bold">Content Type</h6>
                <p>{lookups.types[request.content_type_id] || "Standard"}</p>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <HistoryModal
        entityType="request"
        entityId={request.id}
        title={`Request #${request.request_number} History`}
        show={showHistory}
        onHide={() => setShowHistory(false)}
      />
    </Container>
  );
}